import React, { useState } from 'react';
import { toast } from './Toast';
import { X, FileText, Copy, Download, CheckCircle, AlertCircle, Lightbulb, Info, Edit, Save, FileOutput } from 'lucide-react';
import { crmApi } from '../services/crmApi';

interface GeneratedCertificate {
  type: string;
  title: string;
  content: string;
  companyName: string;
  companyAddress: string;
  contactPerson: string;
  contactPosition: string;
  issueDate: string;
  instructions: string;
  placeholders: {
    signature: boolean;
    stamp: boolean;
    date: boolean;
  };
}

interface CertificatePackage {
  certificates: GeneratedCertificate[];
  coverLetter: string;
  instructions: string;
  tips: string[];
}

interface Props {
  package: CertificatePackage | null;
  onClose: () => void;
}

export const CertificateGeneratorModal: React.FC<Props> = ({ package: pkg, onClose }) => {
  const [selectedCertificate, setSelectedCertificate] = useState<number>(0);
  const [copied, setCopied] = useState<{ [key: number]: boolean }>({});
  const [editingContent, setEditingContent] = useState<{ [key: number]: string }>({});
  const [isEditing, setIsEditing] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  if (!pkg) return null;

  const certificate = pkg.certificates[selectedCertificate];

  // Get the current content (edited or original)
  const getCurrentContent = (index: number) => {
    return editingContent[index] ?? pkg.certificates[index].content;
  };

  const handleCopy = (index: number, text: string) => {
    navigator.clipboard.writeText(text);
    setCopied({ ...copied, [index]: true });
    setTimeout(() => {
      setCopied({ ...copied, [index]: false });
    }, 2000);
  };

  const handleDownload = (cert: GeneratedCertificate, index: number) => {
    const content = `${cert.title}\n\n${cert.companyName}\n${cert.companyAddress}\n\n${getCurrentContent(index)}\n\nData wystawienia: ${cert.issueDate}\n\nInstrukcje:\n${cert.instructions}`;

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${cert.type}_${cert.title.replace(/\s+/g, '_')}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadPdf = async (cert: GeneratedCertificate, index: number) => {
    setGeneratingPdf(true);
    try {
      await crmApi.generateCertificatePdf({
        title: cert.title,
        content: getCurrentContent(index),
        companyName: cert.companyName,
        companyAddress: cert.companyAddress,
        contactPerson: cert.contactPerson,
        contactPosition: cert.contactPosition,
        issueDate: cert.issueDate,
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Błąd podczas generowania PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleToggleEdit = () => {
    if (!isEditing) {
      // Enter edit mode - initialize with current content if not already edited
      if (editingContent[selectedCertificate] === undefined) {
        setEditingContent({
          ...editingContent,
          [selectedCertificate]: certificate.content,
        });
      }
    }
    setIsEditing(!isEditing);
  };

  const getCertificateIcon = (type: string) => {
    switch (type) {
      case 'internship': return '📚';
      case 'volunteer': return '🤝';
      case 'employment': return '💼';
      case 'reference': return '✉️';
      case 'skills': return '🎓';
      default: return '📄';
    }
  };

  const getCertificateTypeLabel = (type: string) => {
    switch (type) {
      case 'internship': return 'Praktyki';
      case 'volunteer': return 'Wolontariat';
      case 'employment': return 'Świadectwo pracy';
      case 'reference': return 'Referencje';
      case 'skills': return 'Kurs/Szkolenie';
      default: return 'Dokument';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-7xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <FileText className="w-8 h-8" />
            <div>
              <h2 className="text-2xl font-bold">Generator Zaświadczeń</h2>
              <p className="text-indigo-100 text-sm mt-1">Edytuj treść i pobierz PDF</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Important Notice */}
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-700 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-bold text-yellow-800">WAŻNE: To są PRZYKŁADOWE szablony!</h3>
              <p className="text-sm text-yellow-700 mt-1">
                Wygenerowane dokumenty to <strong>wzory i szablony</strong>. Możesz edytować treść przed pobraniem PDF.
                <strong> NIE PODRABIAJ</strong> podpisów ani pieczątek!
              </p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto flex">
          {/* Sidebar - Certificate List */}
          <div className="w-64 bg-gray-50 border-r p-4 overflow-y-auto">
            <h3 className="font-bold text-gray-800 mb-3">Dokumenty ({pkg.certificates.length})</h3>
            <div className="space-y-2">
              {pkg.certificates.map((cert, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setSelectedCertificate(index);
                    setIsEditing(false);
                  }}
                  className={`w-full text-left p-3 rounded-lg transition ${
                    selectedCertificate === index
                      ? 'bg-indigo-600 text-white shadow-md'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">{getCertificateIcon(cert.type)}</span>
                    <span className="text-xs font-semibold uppercase">
                      {getCertificateTypeLabel(cert.type)}
                    </span>
                  </div>
                  <div className={`text-sm ${selectedCertificate === index ? 'text-indigo-100' : 'text-gray-600'}`}>
                    {cert.title}
                  </div>
                  {editingContent[index] !== undefined && (
                    <div className={`text-xs mt-1 ${selectedCertificate === index ? 'text-yellow-200' : 'text-yellow-600'}`}>
                      (edytowane)
                    </div>
                  )}
                </button>
              ))}
            </div>

            {/* Quick Actions */}
            <div className="mt-6 pt-4 border-t space-y-2">
              <button
                onClick={() => {
                  const allContent = pkg.certificates
                    .map((cert, i) => `${cert.title}\n\n${getCurrentContent(i)}\n\n${'='.repeat(60)}\n\n`)
                    .join('');
                  handleCopy(-1, allContent);
                }}
                className="w-full bg-indigo-600 text-white py-2 rounded-lg hover:bg-indigo-700 transition text-sm font-medium"
              >
                {copied[-1] ? 'Skopiowano!' : 'Kopiuj wszystkie'}
              </button>
            </div>
          </div>

          {/* Main Content - Certificate Preview/Edit */}
          <div className="flex-1 p-6 overflow-y-auto">
            {certificate && (
              <div>
                {/* Certificate Header */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">{certificate.title}</h2>
                      <p className="text-sm text-gray-600 mt-1">
                        {getCertificateIcon(certificate.type)} {getCertificateTypeLabel(certificate.type)}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={handleToggleEdit}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                          isEditing
                            ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                            : 'bg-gray-600 text-white hover:bg-gray-700'
                        }`}
                      >
                        {isEditing ? <Save className="w-4 h-4" /> : <Edit className="w-4 h-4" />}
                        {isEditing ? 'Zakończ edycję' : 'Edytuj treść'}
                      </button>
                      <button
                        onClick={() => handleCopy(selectedCertificate, getCurrentContent(selectedCertificate))}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                      >
                        {copied[selectedCertificate] ? (
                          <><CheckCircle className="w-4 h-4" /> Skopiowano!</>
                        ) : (
                          <><Copy className="w-4 h-4" /> Kopiuj</>
                        )}
                      </button>
                      <button
                        onClick={() => handleDownload(certificate, selectedCertificate)}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                      >
                        <Download className="w-4 h-4" />
                        TXT
                      </button>
                      <button
                        onClick={() => handleDownloadPdf(certificate, selectedCertificate)}
                        disabled={generatingPdf}
                        className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50"
                      >
                        <FileOutput className="w-4 h-4" />
                        {generatingPdf ? 'Generowanie...' : 'PDF'}
                      </button>
                    </div>
                  </div>

                  {/* Company Info */}
                  <div className="bg-gray-50 rounded-lg p-4 border">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-semibold text-gray-700">Firma/Organizacja:</span>
                        <p className="text-gray-900">{certificate.companyName}</p>
                      </div>
                      <div>
                        <span className="font-semibold text-gray-700">Adres:</span>
                        <p className="text-gray-900">{certificate.companyAddress}</p>
                      </div>
                      <div>
                        <span className="font-semibold text-gray-700">Osoba kontaktowa:</span>
                        <p className="text-gray-900">{certificate.contactPerson}</p>
                      </div>
                      <div>
                        <span className="font-semibold text-gray-700">Stanowisko:</span>
                        <p className="text-gray-900">{certificate.contactPosition}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Certificate Content — Editable or Preview */}
                {isEditing ? (
                  <div className="mb-6">
                    <div className="bg-yellow-50 border border-yellow-300 rounded-t-lg p-2 text-sm text-yellow-800 flex items-center gap-2">
                      <Edit className="w-4 h-4" />
                      Tryb edycji — możesz poprawić treść przed pobraniem PDF
                    </div>
                    <textarea
                      value={getCurrentContent(selectedCertificate)}
                      onChange={(e) => setEditingContent({
                        ...editingContent,
                        [selectedCertificate]: e.target.value,
                      })}
                      className="w-full border-2 border-yellow-300 rounded-b-lg p-6 font-serif text-gray-900 leading-relaxed focus:outline-none focus:border-yellow-500"
                      rows={20}
                      style={{ minHeight: '400px' }}
                    />
                  </div>
                ) : (
                  <div className="bg-white border-2 border-gray-200 rounded-lg p-8 mb-6 font-serif">
                    <pre className="whitespace-pre-wrap text-gray-900 leading-relaxed">
                      {getCurrentContent(selectedCertificate)}
                    </pre>
                  </div>
                )}

                {/* Placeholders Info */}
                {(certificate.placeholders.signature || certificate.placeholders.stamp || certificate.placeholders.date) && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <h4 className="font-bold text-blue-800 mb-2">Pola do uzupełnienia:</h4>
                    <ul className="text-sm text-blue-900 space-y-1">
                      {certificate.placeholders.signature && <li>- Wymagany podpis osoby uprawnionej</li>}
                      {certificate.placeholders.stamp && <li>- Wymagana pieczęć firmowa/organizacji</li>}
                      {certificate.placeholders.date && <li>- Wymagane uzupełnienie daty</li>}
                    </ul>
                  </div>
                )}

                {/* Instructions */}
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h4 className="font-bold text-purple-800 mb-2 flex items-center gap-2">
                    <Info className="w-5 h-5" />
                    Instrukcje użycia:
                  </h4>
                  <p className="text-sm text-purple-900 whitespace-pre-line">{certificate.instructions}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 border-t">
          <div className="p-4 max-h-48 overflow-y-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2 text-sm">
                  <FileText className="w-4 h-4 text-indigo-600" />
                  Ogólne Instrukcje
                </h3>
                <div className="text-xs text-gray-700 whitespace-pre-line bg-white rounded-lg p-3 border max-h-32 overflow-y-auto">
                  {pkg.instructions}
                </div>
              </div>
              <div>
                <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2 text-sm">
                  <Lightbulb className="w-4 h-4 text-yellow-600" />
                  Wskazówki
                </h3>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {pkg.tips.map((tip, index) => (
                    <div key={index} className="bg-white rounded p-2 border text-xs text-gray-700">
                      {tip}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="p-4 bg-white border-t flex justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition font-medium"
            >
              Zamknij
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
